import { makeObservable, observable, computed, action } from 'mobx'
import { Cubit } from './core'
import { RoomStateStatus, RoomState } from '../models/RoomState'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { generateUniqueGroupNames } from '../sources-of-truth/defaultGroupNames'
import {
  AssignmentGroupingType,
  type SectionAssignment,
} from '../models/SectionAssignment'

export class NewRoomStateCubit extends Cubit {
  repository: FirebaseRepository

  @observable dayFilter: 'all' | 'm' | 't' | 'w' | 'th' | 'f' | 's' | 'su' =
    'all'

  @observable timeFilter: 'all' | 'morning' | 'afternoon' | 'evening' = 'all'

  @observable searchString: string = ''

  @observable roomIdToJoin: string | null = null

  constructor(repository: FirebaseRepository) {
    super()
    this.repository = repository
    makeObservable(this)
  }

  @action
  setDayFilter(d: typeof this.dayFilter) {
    this.dayFilter = d
  }

  @action
  setTimeFilter(t: typeof this.timeFilter) {
    this.timeFilter = t
  }

  @action
  setSearchString(s: typeof this.searchString) {
    this.searchString = s
  }

  @action
  setRoomIdToJoin(id: typeof this.roomIdToJoin) {
    this.roomIdToJoin = id
  }

  filterAndSortRooms({
    roomsWithStatus,
    assignment,
  }: Omit<
    Parameters<typeof this.isRoomLocked>[0],
    'room' | 'roomStateStatus'
  > & {
    roomsWithStatus: {
      room: RoomState
      status: RoomStateStatus
    }[]
  }) {
    // needed to sort by number in virtual name
    const roomNameSortingCollator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: 'base',
    })

    const roomsMap = new Map(roomsWithStatus.map((d) => [d.room.id, d]))

    const roomsWithoutCurrentRoom = new Map(roomsMap)

    const names = generateUniqueGroupNames(
      this.repository,
      roomsWithStatus.length + 1,
      Array.from(roomsWithoutCurrentRoom.values()).map(
        (r) => r.room.data.roomStateName || ''
      )
    )

    const roomsFilteredAndSorted = roomsWithStatus
      .map((r) => {
        // remove current room from the list of rooms to get a unique name
        roomsWithoutCurrentRoom.delete(r.room.id)

        return {
          room: r.room,
          status: r.status,
          virtualName: r.room.data.roomStateName || names.pop() || '',
        }
      })
      .filter(
        ({ room }) =>
          !room.isFlaggedForDeletion &&
          this.roomMatchesSearchFilter(room) &&
          this.roomMatchesTimeFilter(room) &&
          this.roomMatchesDayFilter(room)
      )
      .sort((a, b) => {
        // todo: verify with kareem, disable non-empty priority until then
        // empty groups should always be sorted last
        // const aUsers = a.room.userIds.length
        // const bUsers = b.room.userIds.length
        // if ((!aUsers || !bUsers) && aUsers !== bUsers) return bUsers - aUsers

        const aTime = a.room.data.scheduledAt?.getTime()
        const bTime = b.room.data.scheduledAt?.getTime()

        // if one room is scheduled and other not, put scheduled room first
        if (aTime && !bTime) return -1
        if (!aTime && bTime) return 1
        if (aTime && bTime && aTime !== bTime) return aTime - bTime

        // if can't compare times, compare group name
        const aName = a.room.simpleRoomName || a.virtualName
        const bName = b.room.simpleRoomName || b.virtualName
        return roomNameSortingCollator.compare(aName, bName)
      })

    const { lockedRooms, unlockedRooms } = roomsFilteredAndSorted.reduce<{
      lockedRooms: typeof roomsFilteredAndSorted
      unlockedRooms: typeof roomsFilteredAndSorted
    }>(
      (acc, r) => {
        if (
          this.isRoomLocked({
            room: r.room,
            roomStateStatus: r.status,
            assignment,
          })
        )
          acc.lockedRooms.push(r)
        else acc.unlockedRooms.push(r)
        return acc
      },
      {
        lockedRooms: [],
        unlockedRooms: [],
      }
    )

    // always add the phantom room as we want to use its virtual name for new group creation
    // we decide whether we want to display it or not from the client
    const phantomRoom = RoomState.empty(this.repository)
    const roomWithVirtualName = {
      room: phantomRoom,
      virtualName: names.pop() || '',
      status: RoomStateStatus.scheduled, // status shouldn't matter for phantom room
    }
    unlockedRooms.push(roomWithVirtualName)

    return { lockedRooms, unlockedRooms }
  }

  @computed
  get anyActiveFilters() {
    return (
      this.dayFilter !== 'all' ||
      this.timeFilter !== 'all' ||
      this.searchString.trim().length > 0
    )
  }

  private isRoomLocked({
    room,
    assignment,
    roomStateStatus,
  }: {
    room: RoomState
    assignment: SectionAssignment
    roomStateStatus: RoomStateStatus
  }) {
    const threshold =
      assignment.groupingType === AssignmentGroupingType.manual
        ? assignment.maxGroupSize
        : assignment.desiredGroupSize

    return (
      room.hasMaxUsers(threshold) ||
      [
        RoomStateStatus.expired,
        RoomStateStatus.completed,
        RoomStateStatus.abandoned,
        RoomStateStatus.queuedForGrading,
      ].includes(roomStateStatus)
    )
  }

  private roomMatchesSearchFilter(room: RoomState) {
    if (!this.searchString) return true
    const searchTerm = this.searchString.toLowerCase().trim()
    const userNamesWithRoomName =
      room.users.map((user) => user.fullName).join(' ') +
      (room.data.roomStateName || '')
    return userNamesWithRoomName.toLowerCase().includes(searchTerm)
  }

  private roomMatchesTimeFilter(room: RoomState) {
    // all = no filter
    if (this.timeFilter === 'all') return true

    if (!room.data.scheduledAt) return false

    const hour = room.data.scheduledAt.getHours()

    // morning midnight to 11am
    if (this.timeFilter === 'morning') return hour < 11

    // afternoon 11am to 5pm
    if (this.timeFilter === 'afternoon') return hour >= 11 && hour < 17

    // evening 5pm to midnight
    if (this.timeFilter === 'evening') return hour >= 17
    return false
  }

  private roomMatchesDayFilter(room: RoomState) {
    // all = no filter
    if (this.dayFilter === 'all') return true

    if (!room.data.scheduledAt) return false

    const day = room.data.scheduledAt.getDay()

    const dayToNumber = {
      m: 1,
      t: 2,
      w: 3,
      th: 4,
      f: 5,
      s: 6,
      su: 0,
    }

    return day === dayToNumber[this.dayFilter]
  }
}
